<template>
    <div>
        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('users.headline.user_information') }}
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input
                        name="username"
                        v-model="fields.username"
                        :placeholder="user && user.username ? user.username : trans('users.label.username_will_be_generated')"
                        disabled="disabled"
                    >
                        {{ trans('users.label.username') }}
                    </form-input>

                </div>

                <div class="form-row__col" v-if="user && user.client">
                    <form-input name="client" v-model="user.client.name" disabled="disabled">
                        {{ trans('users.label.client') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <label class="label" for="gender">{{ trans('users.label.salutation') }}</label>
                    <select name="gender" v-model="fields.gender" :class="['form-select', {'form-select--error': errors('gender')}]" :disabled="canNotEdit">
                        <option disabled>{{ trans('users.label.select') }}</option>
                        <option value="">{{ trans('users.label.not_specified') }}</option>
                        <option :value="gender" v-for="gender in genders" :key="gender">{{ trans('users.label.' + gender) }}</option>
                    </select>
                    <span class="invalid-feedback" v-if="errors('gender')">
                        <strong>{{ errors('gender') }}</strong>
                    </span>
                </div>

                <div class="form-row__col">
                    <form-input name="title" v-model="fields.title" :disabled="canNotEdit">
                        {{ trans('users.label.title') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input name="firstname" :required="true" v-model="fields.firstname" :error="validate('firstname')" :disabled="canNotEdit">
                        {{ trans('users.label.firstname') }}
                    </form-input>

                </div>
                <div class="form-row__col">
                    <form-input name="lastname" :required="true" v-model="fields.lastname" :error="validate('lastname')" :disabled="canNotEdit">
                        {{ trans('users.label.lastname') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row" v-if="nameChanged && canChangeUsername">
                <div class="form-row__col form-row__col--one-half">
                    <checkbox name="change_username" value="true">
                        {{ trans('users.label.change_username') }}
                    </checkbox>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <form-input name="street" v-model="fields.street" :disabled="canNotEdit">
                        {{ trans('users.label.street') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input name="zip" v-model="fields.zip" :disabled="canNotEdit">
                        {{ trans('users.label.zip') }}
                    </form-input>
                </div>
                <div class="form-row__col">
                    <form-input name="city" v-model="fields.city" :disabled="canNotEdit">
                        {{ trans('users.label.city') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input name="email" :required="true" v-model="fields.email" :error="validate('email')" :disabled="canNotEdit">
                        {{ trans('users.label.email') }}
                    </form-input>
                </div>

                <div class="form-row__col">
                    <form-input name="internal_id" v-model="fields.internal_id" :disabled="canNotEdit">
                        {{ trans('users.label.internal_id') }}
                    </form-input>
                </div>
            </div>
        </section>

        <section class="form-section" v-if="canNotChangeRole == false">
            <label class="form-section-heading">
                {{ trans('users.label.role') }}
            </label>

            <checkbox-wrapper name="roles[]" :error="validate('roles')">
                <checkbox-card :card="card" icon="/images/icon-user.svg" name="roles[]" v-for="(card, index) in clientRolesCheckboxes" :disabled="canNotChangeRole" :key="index"></checkbox-card>
            </checkbox-wrapper>
        </section>

        <section class="form-section" v-if="fields.specificUserFields.length">
            <div class="form-section-heading">
                {{ trans('users.headline.client_specific_fields') }}
            </div>

            <extendable-card-list
                v-model="fields.specificUserFields"
                v-slot="slot"
                :cards="fields.specificUserFields"
                data-can-be-edited
            >
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        :name="`specific_user_fields[${slot.data.key}]`"
                        :required="slot.data.required"
                        v-model="slot.data.value"
                        :error="validate(`specific_user_fields.${slot.data.key}`)"
                        :disabled="canNotEdit"
                    >
                        {{ slot.data.label }}
                    </form-input>
                </div>
            </extendable-card-list>
        </section>
    </div>
</template>

<script>
import { EventBus } from '../eventbus.js';

export default {
    model: {
        prop: 'userFormFields',
        event: 'bubbleUserFormFields'
    },
    props: {
        'client': {
            type: Object,
            default: () => { return {}; },
        },
        'user': {
            type: Object,
            default: () => { return {}; },
        },
        'formerrors': {
            type: Object,
            default: () => { return {}; },
        },
        'canNotChangeRole': {
            type: Boolean,
        },
        'canNotEdit': {
            type: Boolean,
        },
        'canChangeUsername': {
            type: Boolean,
        },
    },

    data() {
        return {
            fields: {
                roles: (Object.entries(this.user).length === 0 && this.user.constructor === Object) ? [] : this.user.roles_id,
                gender: this.old('gender', this.user.gender),
                title: this.old('title', this.user.title),
                firstname: this.old('firstname', this.user.firstname),
                lastname: this.old('lastname', this.user.lastname),
                username: this.old('username', this.user.username),
                email: this.old('email', this.user.email),
                street: this.old('street', this.user.street),
                zip: this.old('zip', this.user.zip),
                city: this.old('city', this.user.city),
                internal_id: this.old('internal_id', this.user.internal_id),
                specificUserFields: [],
            },
            genders: [
                'male',
                'female',
                'other',
            ],
        };
    },

    computed: {
        nameChanged() {
            if (this.user) {
                var username = this.fields.username.split('.');

                // Find firstname in username
                var firstnameEqual = username.find((value) => {
                    return value == this.fields.firstname.toLowerCase();
                });

                // Find lastname in username
                var lastnameEqual = username.find((value) => {
                    return value == this.fields.lastname.toLowerCase();
                });

                // Username can be updated when the fields are updated by user or if firstname and lastname dont match the current username
                if (this.user.firstname != this.fields.firstname || this.user.lastname != this.fields.lastname || !firstnameEqual || !lastnameEqual) {
                    return true;
                }
            }
            return false;
        },
        clientRolesCheckboxes() {
            let roles = [
                {
                    'value': '8',
                    'title': this.trans('users.role.supervisor'),
                    'description': this.descriptionText(this.trans('users.role_description.supervisor')),
                    'status': this.fields.roles.includes(8),
                },
                {
                    'value': '6',
                    'title': this.trans('users.role.controller'),
                    'description': this.descriptionText(this.trans('users.role_description.controller')),
                    'status': this.fields.roles.includes(6),
                },
                {
                    'value': '7',
                    'title': this.trans('users.role.end_user'),
                    'description': this.descriptionText(this.trans('users.role_description.end_user')),
                    'status': this.fields.roles.includes(7),
                },
            ];

            if (!this.currentUser.isAdmin() && !this.currentUser.isClientAdmin()) {
                return roles;
            }

            return [
                {
                    'value': '5',
                    'title': this.trans('users.role.client_admin'),
                    'description': this.descriptionText(this.trans('users.role_description.client_admin')),
                    'status': this.fields.roles.includes(5),
                },
                ...roles
            ];
        },
        /**
         * Summerizes all specific user fields to one field.
         */
        specificUserFields() {
            var fieldsArray = [];

            for (var field of this.fields.specificUserFields) {
                fieldsArray.push(field);
            }
            return JSON.stringify(fieldsArray);
        },
    },

    methods: {

        descriptionText(descriptionsArray) {
            var descriptionHtml = "<ul>";

            descriptionsArray.forEach(descriptionLine => {
                descriptionHtml += "<li>" + descriptionLine + "</li>";
            });

            return (descriptionHtml + "</ul>");
        },

        buildSpecificUserFields() {
            var oldUserFields = [];
            // if we come back from error, restructure oldUserFields first
            if (this.old('specific_user_fields')) {
                var tempOldUserFields = this.old('specific_user_fields');
                var i = 0;
                for (var key in tempOldUserFields) {
                    oldUserFields[i] = {};
                    oldUserFields[i][key] = tempOldUserFields[key];
                    i++;
                }
            } else {
                oldUserFields = this.user.specific_user_fields || [];
            }

            var userFieldsArray = [];
            var clientFields = this.client.specific_user_fields.data || this.client.specific_user_fields;
            clientFields.forEach(function (field, index) {
                userFieldsArray[index] = {};
                userFieldsArray[index].key = field.key;
                userFieldsArray[index].label = field.name;
                userFieldsArray[index].required = field.required == 'true';

                // search for key
                var item = oldUserFields.find(item => item[userFieldsArray[index].key]);
                if (item) {
                    userFieldsArray[index].value = item[userFieldsArray[index].key];
                    return 1;
                }

                // default
                userFieldsArray[index].value = "";
            });
            return userFieldsArray;
        },

        validate(attribute) {
            return this.formerrors[attribute] ? this.formerrors[attribute][0] : ''
        }
    },

    mounted() {
        this.fields.specificUserFields = this.buildSpecificUserFields();

        /**
         * This event has to be catched from the final step component to post the data to backend.
         * Data parameter has to be expanded by the further fields (licenses, courses).
         */
        if (this.user) {
            EventBus.$on('validationSuccessful', () => {
                // Submit userform to backend
                document.getElementById('user-form').submit();
            });
        }

        this.$emit('bubbleUserFormFields', this.fields);

        EventBus.$on('checkboxesChanged', (roles) => {
            if (roles.name == 'roles[]') {
                this.fields.roles = roles.data.filter(array=>{
                    return array.status == true
                }).map(array => {
                    return array.value
                });
            }
        });
    },

    watch: {
        fields: {
            handler() {
                this.$emit('bubbleUserFormFields', this.fields);
            },
            deep: true,
        },
    },
};
</script>
